import { useMemo } from 'react';

import dayjs, { Dayjs } from 'dayjs';

import { VisibleDateRange } from '../../types';

interface VisibleDateWindowParams {
  dates: Dayjs[];
  visibleDateRange: VisibleDateRange;
  cellWidth: number;
}

interface VisibleDateWindow {
  startIndex: number;
  endIndex: number;
  visibleDates: dayjs.Dayjs[];
  leftSpacerWidth: number;
  rightSpacerWidth: number;
}

/**
 * Clamps a visible date range to valid indices and computes spacer widths
 * for horizontal virtualization of timeline cells.
 */
export const useVisibleDateWindow = ({
  dates,
  visibleDateRange,
  cellWidth,
}: VisibleDateWindowParams): VisibleDateWindow => {
  const maxIndex = Math.max(0, dates.length - 1);
  const startIndex = Math.min(Math.max(visibleDateRange.startIndex, 0), maxIndex);
  const endIndex = Math.min(Math.max(visibleDateRange.endIndex, startIndex), maxIndex);

  const visibleDates = useMemo(
    () => dates.slice(startIndex, endIndex + 1),
    [dates, startIndex, endIndex]
  );

  const leftSpacerWidth = startIndex * cellWidth;
  const rightSpacerWidth = Math.max(0, dates.length - endIndex - 1) * cellWidth;

  return {
    startIndex,
    endIndex,
    visibleDates,
    leftSpacerWidth,
    rightSpacerWidth,
  };
};
